import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { CommandeApi } from '../../types';
import styles from './Commandes.module.css';

const STATUT_PAIEMENT_OPTIONS = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'paye', label: 'Payé' },
  { value: 'echec', label: 'Échec' },
  { value: 'rembourse', label: 'Remboursé' },
];

const STATUT_LIVRAISON_OPTIONS = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'en_preparation', label: 'En préparation' },
  { value: 'depose_transporteur', label: 'Déposé transporteur' },
  { value: 'en_transit', label: 'En transit' },
  { value: 'disponible_point_relais', label: 'Dispo point relais' },
  { value: 'livre', label: 'Livré' },
];

export const Commandes: React.FC = () => {
  const navigate = useNavigate();
  const [commandes, setCommandes] = useState<CommandeApi[]>([]);
  const [filtered, setFiltered] = useState<CommandeApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterPaiement, setFilterPaiement] = useState('');
  const [filterLivraison, setFilterLivraison] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCommandes();
  }, []);

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commandes, filterPaiement, filterLivraison, search]);

  const loadCommandes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get<CommandeApi[]>('commandes');
      setCommandes(response.data ?? []);
    } catch (err: any) {
      const apiError = err.response?.data?.error || err.response?.data?.detail || err.response?.data?.message || '';
      setError('Erreur lors du chargement : ' + (apiError || err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...commandes];
    if (filterPaiement) result = result.filter((c) => c.statutPaiement === filterPaiement);
    if (filterLivraison) result = result.filter((c) => c.statutLivraison === filterLivraison);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.nomClient?.toLowerCase().includes(q) ||
          c.prenomClient?.toLowerCase().includes(q) ||
          c.emailClient?.toLowerCase().includes(q) ||
          String(c.id).includes(q)
      );
    }
    setFiltered(result);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(c => c.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(
      `Supprimer ${selectedIds.size} commande(s) sélectionnée(s) ?\nCette action est irréversible.`
    );
    if (!confirmed) return;
    try {
      setDeleting(true);
      await api.delete('commandes/bulk', { data: Array.from(selectedIds) });
      setSelectedIds(new Set());
      await loadCommandes();
    } catch (err: any) {
      alert('Erreur lors de la suppression : ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (
    commandeId: number,
    field: 'statutPaiement' | 'statutLivraison',
    value: string
  ) => {
    try {
      await api.put(`commandes/${commandeId}/status`, { [field]: value });
      setCommandes((prev) =>
        prev.map((c) => (c.id === commandeId ? { ...c, [field]: value } : c))
      );
    } catch (err: any) {
      alert('Erreur mise à jour statut : ' + (err.response?.data?.message || err.message));
    }
  };

  const getBadgePaiement = (statut: string) => {
    switch (statut) {
      case 'paye':      return <span className={`${styles.badge} ${styles.badgeGreen}`}>✓ Payé</span>;
      case 'en_attente':return <span className={`${styles.badge} ${styles.badgeOrange}`}>⏳ En attente</span>;
      case 'echec':     return <span className={`${styles.badge} ${styles.badgeRed}`}>✕ Échec</span>;
      case 'rembourse': return <span className={`${styles.badge} ${styles.badgeBlue}`}>↩ Remboursé</span>;
      default:          return <span className={styles.badge}>{statut}</span>;
    }
  };

  const getBadgeLivraison = (statut: string) => {
    switch (statut) {
      case 'livre':                  return <span className={`${styles.badge} ${styles.badgeGreen}`}>✓ Livré</span>;
      case 'en_preparation':         return <span className={`${styles.badge} ${styles.badgePurple}`}>📦 En préparation</span>;
      case 'en_attente':             return <span className={`${styles.badge} ${styles.badgeGray}`}>En attente</span>;
      case 'depose_transporteur':    return <span className={`${styles.badge} ${styles.badgeBlue}`}>🚚 Déposé</span>;
      case 'en_transit':             return <span className={`${styles.badge} ${styles.badgeBlue}`}>🚚 En transit</span>;
      case 'disponible_point_relais':return <span className={`${styles.badge} ${styles.badgeTeal}`}>📍 Dispo relais</span>;
      default:                       return <span className={styles.badge}>{statut}</span>;
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n ?? 0);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const caFiltered = filtered.reduce((sum, c) => sum + (c.total ?? 0), 0);

  return (
    <Layout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>Commandes</h1>
          <div className={styles.headerActions}>
            {selectedIds.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className={styles.btnDeleteSelected}
                disabled={deleting}
              >
                {deleting ? 'Suppression...' : `🗑 Supprimer (${selectedIds.size})`}
              </button>
            )}
            <button onClick={loadCommandes} className={styles.btnRefresh} disabled={loading}>
              {loading ? 'Chargement...' : '↻ Actualiser'}
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className={styles.filtersBar}>
          <input
            type="text"
            placeholder="Rechercher (nom, email, #id)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          <select
            value={filterPaiement}
            onChange={(e) => setFilterPaiement(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">Tous les paiements</option>
            {STATUT_PAIEMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={filterLivraison}
            onChange={(e) => setFilterLivraison(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">Toutes les livraisons</option>
            {STATUT_LIVRAISON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className={styles.filterStats}>
            <span>{filtered.length} commande(s)</span>
            <span className={styles.filterCa}>CA : {fmt(caFiltered)}</span>
          </div>
          {(filterPaiement || filterLivraison || search) && (
            <button
              className={styles.btnClear}
              onClick={() => { setFilterPaiement(''); setFilterLivraison(''); setSearch(''); }}
            >
              ✕ Effacer
            </button>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Tableau */}
        {!loading && filtered.length === 0 ? (
          <div className={styles.noData}>Aucune commande trouvée.</div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>#</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Livraison</th>
                  <th>Paiement</th>
                  <th>Statut paiement</th>
                  <th>Statut livraison</th>
                  <th>Détails</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((commande) => (
                  <tr
                    key={commande.id}
                    className={`${styles.row} ${selectedIds.has(commande.id) ? styles.selectedRow : ''}`}
                    onDoubleClick={() => navigate(`/commandes/${commande.id}`)}
                  >
                    <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(commande.id)}
                        onChange={() => toggleSelect(commande.id)}
                      />
                    </td>
                    <td className={styles.colId}>#{commande.id}</td>
                    <td>
                      <div className={styles.clientName}>
                        {commande.prenomClient} {commande.nomClient}
                      </div>
                      <div className={styles.clientEmail}>{commande.emailClient}</div>
                    </td>
                    <td className={styles.colDate}>{fmtDate(commande.dateCommande)}</td>
                    <td className={styles.colPrice}>{fmt(commande.total)}</td>
                    <td>
                      {commande.modeLivraison === 'mondial_relay'
                        ? '📦 Mondial Relay'
                        : '🏠 Domicile'}
                    </td>
                    <td className={styles.colMethPay}>{commande.methodePaiement}</td>

                    {/* Statut paiement */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className={styles.statusCell}>
                        {getBadgePaiement(commande.statutPaiement)}
                        <select
                          value={commande.statutPaiement}
                          onChange={(e) =>
                            handleStatusChange(commande.id, 'statutPaiement', e.target.value)
                          }
                          className={styles.statusSelect}
                        >
                          {STATUT_PAIEMENT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>

                    {/* Statut livraison */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className={styles.statusCell}>
                        {getBadgeLivraison(commande.statutLivraison)}
                        <select
                          value={commande.statutLivraison}
                          onChange={(e) =>
                            handleStatusChange(commande.id, 'statutLivraison', e.target.value)
                          }
                          className={styles.statusSelect}
                        >
                          {STATUT_LIVRAISON_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>

                    {/* Bouton Voir */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className={styles.btnVoir}
                        onClick={() => navigate(`/commandes/${commande.id}`)}
                      >
                        👁 Voir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};
